import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  ScrollText, Search, Filter, ArrowUpDown, ExternalLink,
  GitBranch, UserCheck, AlertTriangle, RefreshCw, Clock
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import Layout from "../components/Layout";
import { API } from "../App";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const EVENT_TYPES = [
  { value: "initial_assignment", label: "Initial Assignment", color: "bg-blue-100 text-blue-700", icon: GitBranch },
  { value: "reassignment", label: "Reassignment", color: "bg-amber-100 text-amber-700", icon: RefreshCw },
  { value: "transition", label: "Transition", color: "bg-violet-100 text-violet-700", icon: ArrowUpDown },
  { value: "escalation", label: "Escalation", color: "bg-red-100 text-red-700", icon: AlertTriangle },
  { value: "sla_breach", label: "SLA Breach", color: "bg-red-100 text-red-800", icon: Clock },
];

const RoutingLogsPage = () => {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchLeadId, setSearchLeadId] = useState("");
  const [filterEvent, setFilterEvent] = useState("all");
  const [limit, setLimit] = useState(50);
  const navigate = useNavigate();

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (searchLeadId.trim()) params.append("lead_id", searchLeadId.trim());
      if (filterEvent !== "all") params.append("event_type", filterEvent);
      params.append("limit", limit);

      const res = await axios.get(`${API}/routing/logs?${params.toString()}`);
      setLogs(res.data.logs || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      toast.error("Failed to load routing logs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLogs(); }, [filterEvent, limit]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchLogs();
  };

  const getEventInfo = (type) => EVENT_TYPES.find(e => e.value === type) || EVENT_TYPES[0];

  const formatDate = (d) => {
    if (!d) return "—";
    try {
      return new Date(d).toLocaleString("en-IN", {
        day: "2-digit", month: "short", year: "numeric",
        hour: "2-digit", minute: "2-digit"
      });
    } catch { return d; }
  };

  // Stats
  const byEvent = EVENT_TYPES.map(et => ({
    ...et,
    count: logs.filter(l => l.event_type === et.value).length
  }));

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="routing-logs-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 font-['Manrope']">Routing Logs</h1>
            <p className="text-sm text-slate-500 mt-1">Track every routing decision — who, why, and when</p>
          </div>
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <ScrollText className="w-4 h-4" />
            <span data-testid="logs-total">{total} total entries</span>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {byEvent.map((et) => {
            const Icon = et.icon;
            return (
              <Card key={et.value} className={`cursor-pointer transition-shadow hover:shadow-md ${filterEvent === et.value ? 'ring-2 ring-blue-400' : ''}`}
                onClick={() => setFilterEvent(filterEvent === et.value ? "all" : et.value)}>
                <CardContent className="p-3 flex items-center gap-3">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${et.color}`}>
                    <Icon className="w-4 h-4" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-500">{et.label}</p>
                    <p className="text-lg font-bold text-slate-900">{et.count}</p>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Filters */}
        <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input placeholder="Search by Lead ID..." value={searchLeadId} onChange={(e) => setSearchLeadId(e.target.value)}
              className="pl-10" data-testid="logs-search" />
          </div>
          <Select value={filterEvent} onValueChange={setFilterEvent}>
            <SelectTrigger className="w-48" data-testid="logs-event-filter">
              <Filter className="w-4 h-4 mr-2 text-slate-400" /><SelectValue placeholder="Event Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Events</SelectItem>
              {EVENT_TYPES.map(e => <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button type="submit" variant="outline" data-testid="logs-search-btn">
            <Search className="w-4 h-4 mr-1" /> Search
          </Button>
        </form>

        {/* Logs Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-12 text-center text-slate-400">Loading logs...</div>
            ) : logs.length === 0 ? (
              <div className="p-12 text-center text-slate-400">
                <ScrollText className="w-10 h-10 mx-auto mb-3 opacity-40" />
                <p>No routing logs found.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Time</TableHead>
                      <TableHead>Event</TableHead>
                      <TableHead>Lead</TableHead>
                      <TableHead>Rule</TableHead>
                      <TableHead>Assigned To</TableHead>
                      <TableHead>Method</TableHead>
                      <TableHead>Reason</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {logs.map((log) => {
                      const eventInfo = getEventInfo(log.event_type);
                      const Icon = eventInfo.icon;
                      return (
                        <TableRow key={log.log_id} data-testid={`log-row-${log.log_id}`}>
                          <TableCell className="text-xs text-slate-500 whitespace-nowrap">{formatDate(log.created_at)}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className={`${eventInfo.color} text-xs`}>
                              <Icon className="w-3 h-3 mr-1" />{eventInfo.label}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <button onClick={() => navigate(`/leads/${log.lead_id}`)}
                              className="text-sm text-blue-600 hover:underline font-mono" data-testid={`log-lead-link-${log.log_id}`}>
                              {log.lead_id?.slice(0, 16)}...
                            </button>
                          </TableCell>
                          <TableCell className="text-sm text-slate-700">{log.rule_name || "—"}</TableCell>
                          <TableCell>
                            <div className="text-sm">
                              {log.assigned_user && <span className="text-slate-800">{log.assigned_user.slice(0, 12)}...</span>}
                              {log.previous_user && (
                                <span className="text-xs text-slate-400 block">from: {log.previous_user.slice(0, 12)}...</span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="secondary" className="text-xs">{log.assignment_method || "—"}</Badge>
                          </TableCell>
                          <TableCell className="text-xs text-slate-500 max-w-48 truncate">{log.reason || "—"}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Load More */}
        {logs.length > 0 && logs.length < total && (
          <div className="text-center">
            <Button variant="outline" onClick={() => setLimit(limit + 50)} data-testid="logs-load-more">
              Load More ({total - logs.length} remaining)
            </Button>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default RoutingLogsPage;
